import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ArrowLeft } from "lucide-react";

export default function MfaSetup() {
  const nav = useNavigate();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/auth/mfa/status");
        setStatus(s.data);
        if (s.data.enabled) return;
        const r = await api.post("/auth/mfa/setup");
        setSetup(r.data);
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start MFA setup"); }
    })();
  }, []);

  const enable = async () => {
    if (!/^\d{6}$/.test(code.trim())) { toast.error("Enter the 6-digit code from your authenticator."); return; }
    setLoading(true);
    try {
      await api.post("/auth/mfa/enable", { secret: setup.secret, code: code.trim() });
      toast.success("MFA enabled. From now on you'll need a code at sign-in.");
      nav("/admin");
    } catch (e) { toast.error(e?.response?.data?.detail || "Enable failed"); }
    finally { setLoading(false); }
  };

  if (status?.enabled) {
    return (
      <div className="max-w-lg mx-auto pt-24 px-6 text-center" data-testid="mfa-already-enabled">
        <ShieldCheck className="w-10 h-10 text-emerald-600 mx-auto" />
        <h1 className="mt-3 text-2xl font-bold text-slate-900">MFA is already active</h1>
        <p className="mt-2 text-slate-600 text-sm">You already have a TOTP authenticator enrolled on this account. To re-enroll, disable MFA first.</p>
        <Link to="/admin" className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Back to admin</Link>
      </div>
    );
  }
  if (!setup) return <div className="pt-24 text-center"><Loader2 className="w-8 h-8 text-amber-600 mx-auto animate-spin" /></div>;

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=${encodeURIComponent(setup.otpauth_uri)}`;

  return (
    <div className="max-w-lg mx-auto pt-24 px-6" data-testid="mfa-setup-page">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-1"><ShieldCheck className="w-4 h-4" /> Two-factor authentication</div>
      <h1 className="text-3xl font-bold text-slate-900">Enable authenticator app</h1>
      <p className="mt-2 text-sm text-slate-600">Scan the QR code with Google Authenticator, 1Password, Authy, or any TOTP app. Then enter the 6-digit code to confirm.</p>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 flex flex-col items-center">
        <img src={qrSrc} alt="TOTP QR code" className="w-52 h-52 rounded" data-testid="mfa-qr" />
        <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500">Or enter this key manually</div>
        <div className="font-mono text-xs text-slate-700 mt-1 select-all" data-testid="mfa-secret">{setup.secret}</div>
      </div>

      <label className="block mt-6">
        <span className="block text-sm font-medium text-slate-700 mb-1.5">6-digit code from your app</span>
        <input
          data-testid="mfa-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          placeholder="123456"
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-lg font-mono tracking-widest text-center focus:outline-none focus:border-slate-900"
        />
      </label>

      <button
        data-testid="mfa-enable-btn"
        onClick={enable}
        disabled={loading || code.length !== 6}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Enabling…</> : "Enable MFA"}
      </button>
    </div>
  );
}
